/**
 * Whether the renderer would read this as a colour, asked in the browser so the
 * preview paints what the render will (ADR 000068).
 *
 * The vocabulary is ffmpeg's rather than CSS's, which is the whole point. The
 * two overlap but do not agree, and the render is the authority: a preview that
 * paints a colour ffmpeg refuses has shown somebody a frame nobody can make,
 * and one that drops a colour ffmpeg takes has shown them the wrong bars. So a
 * background note is held to the same list in both places, and neither side is
 * free to be generous with it.
 *
 * The table is a copy, held to the shared fixture by a test rather than by
 * anybody remembering, in the way apps/api/internal/render/colour.go is held to
 * what the pinned ffmpeg actually prints.
 */

/** The colour names ffmpeg takes, lower-cased. */
export const ffmpegColours: readonly string[] = `
  aliceblue antiquewhite aqua aquamarine azure beige bisque black
  blanchedalmond blue blueviolet brown burlywood cadetblue chartreuse
  chocolate coral cornflowerblue cornsilk crimson cyan darkblue darkcyan
  darkgoldenrod darkgray darkgreen darkkhaki darkmagenta darkolivegreen
  darkorange darkorchid darkred darksalmon darkseagreen darkslateblue
  darkslategray darkturquoise darkviolet deeppink deepskyblue dimgray
  dodgerblue firebrick floralwhite forestgreen fuchsia gainsboro ghostwhite
  gold goldenrod gray green greenyellow honeydew hotpink indianred indigo
  ivory khaki lavender lavenderblush lawngreen lemonchiffon lightblue
  lightcoral lightcyan lightgoldenrodyellow lightgreen lightgrey lightpink
  lightsalmon lightseagreen lightskyblue lightslategray lightsteelblue
  lightyellow lime limegreen linen magenta maroon mediumaquamarine
  mediumblue mediumorchid mediumpurple mediumseagreen mediumslateblue
  mediumspringgreen mediumturquoise mediumvioletred midnightblue mintcream
  mistyrose moccasin navajowhite navy oldlace olive olivedrab orange
  orangered orchid palegoldenrod palegreen paleturquoise palevioletred
  papayawhip peachpuff peru pink plum powderblue purple red rosybrown
  royalblue saddlebrown salmon sandybrown seagreen seashell sienna silver
  skyblue slateblue slategray snow springgreen steelblue tan teal thistle
  tomato turquoise violet wheat white whitesmoke yellow yellowgreen
`
  .trim()
  .split(/\s+/);

const colourNames = new Set(ffmpegColours);

/**
 * Whether ffmpeg would read this as a colour.
 *
 * A name from its table, or hex as #rrggbb or #rrggbbaa in either the # or the
 * 0x spelling. Deliberately the same narrowing the renderer applies: reading a
 * spelling it would refuse is how the preview and the render come apart.
 */
export function parsableColour(colour: string): boolean {
  // The prefix is matched as written, because the renderer's is: it takes
  // `0x1b2a4a` and not `0X1b2a4a`, and being generous with a spelling here is
  // how the preview comes to paint what the render refuses. The digits after it
  // are case-insensitive in both.
  for (const prefix of ["#", "0x"]) {
    if (colour.startsWith(prefix)) return hexColour(colour.slice(prefix.length).toLowerCase());
  }
  return colourNames.has(colour.toLowerCase());
}

function hexColour(digits: string): boolean {
  if (digits.length !== 6 && digits.length !== 8) return false;
  return /^[0-9a-f]+$/.test(digits);
}

/**
 * A colour the renderer takes, spelled the way CSS takes it.
 *
 * `parsableColour` answers a question about ffmpeg, and the answer is not
 * always a string a browser can paint. `0x1b2a4a` is half of the hex
 * vocabulary the renderer documents and CSS has no `0x` spelling at all:
 * assigning one leaves the property unset, so the render paints the colour and
 * the preview shows the box its own black (issue #260).
 *
 * That is worse than the fallthrough ADR 000068 describes, because the note was
 * actionable: the resolver stopped at it, nothing further was consulted, and
 * nothing is reported unapplied. The person sees black and no reason for it,
 * and the two spellings are documented as equivalent, so writing the other one
 * is not an obvious thing to try.
 *
 * Only the prefix differs. The digits behind it are what CSS reads already, and
 * every name in the table above is a CSS named colour. Kept apart from
 * `parsableColour`, which is asked what ffmpeg would do and should carry on
 * answering only that.
 */
export function cssColour(colour: string): string {
  return colour.startsWith("0x") ? `#${colour.slice("0x".length)}` : colour;
}
